import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import axiosClient from '../../api/axiosClient';
import styles from './FeatureSwitchesPanel.module.css';

interface Meta { label: string; description: string }
interface SwitchState {
  enabled: boolean;
  userOff?: boolean;
  platformOff?: boolean;
  reason?: string | null;
  platformReason?: string | null;
}
interface HistoryRow {
  feature: string; enabled: boolean; reason: string | null; changed_at: string; changed_by_name: string | null;
}

interface Props {
  // 'user' = one user's switches (needs userId). 'platform' = switches for every user at once.
  mode: 'user' | 'platform';
  userId?: string;
}

const FeatureSwitchesPanel: React.FC<Props> = ({ mode, userId }) => {
  const [features, setFeatures] = useState<Record<string, Meta>>({});
  const [switches, setSwitches] = useState<Record<string, SwitchState>>({});
  const [history, setHistory] = useState<HistoryRow[]>([]);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const base = mode === 'user' ? `/admin/users/${userId}/switches` : '/admin/switches/platform';

  const apply = (data: any) => {
    if (data.features) setFeatures(data.features);
    setSwitches(data.switches || {});
    setHistory(data.history || []);
  };

  const load = useCallback(() => {
    axiosClient.get(base).then(({ data }) => apply(data)).catch(() => {});
  }, [base]);

  useEffect(() => { load(); }, [load]);

  const change = async (feature: string, enabled: boolean, why: string) => {
    setBusy(true);
    try {
      const { data } = await axiosClient.patch(base, { feature, enabled, reason: why });
      apply(data);
      toast.success(`${features[feature]?.label || feature} switched ${enabled ? 'ON' : 'OFF'}.`);
      setConfirming(null);
      setReason('');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not change the switch.');
    } finally {
      setBusy(false);
    }
  };

  const keys = Object.keys(features);
  if (keys.length === 0) return <div className={styles.note}>Loading switches…</div>;

  return (
    <div className={styles.wrap}>
      <div className={styles.note}>
        {mode === 'user'
          ? 'Everything is ON by default. Switching a feature OFF only pauses the action for this user. Earned money is never deleted.'
          : 'Emergency switches. Turning one OFF pauses that feature for EVERY user until you turn it ON again.'}
      </div>
      {keys.map((key) => {
        const s = switches[key] || { enabled: true };
        const ownOn = mode === 'user' ? !s.userOff : s.enabled;
        return (
          <div key={key} className={styles.row}>
            <div className={styles.info}>
              <b>{features[key].label}</b>
              <span>{features[key].description}</span>
              {mode === 'user' && s.userOff && s.reason && <div className={styles.reasonText}>OFF reason: {s.reason}</div>}
              {mode === 'user' && s.platformOff && (
                <div className={styles.platformNote}>Also paused for ALL users{s.platformReason ? `: ${s.platformReason}` : ''}</div>
              )}
              {mode === 'platform' && !s.enabled && s.reason && <div className={styles.reasonText}>OFF reason: {s.reason}</div>}
            </div>
            <div className={styles.right}>
              <span className={`${styles.pill} ${ownOn ? styles.on : styles.off}`}>{ownOn ? 'ON' : 'OFF'}</span>
              {ownOn ? (
                <button className={styles.btn} disabled={busy} onClick={() => { setConfirming(key); setReason(''); }}>Switch OFF</button>
              ) : (
                <button className={styles.btn} disabled={busy} onClick={() => change(key, true, '')}>Switch ON</button>
              )}
            </div>
            {confirming === key && (
              <div className={styles.confirm}>
                <input
                  autoFocus
                  placeholder="Reason for switching OFF (required)"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
                <button className={styles.btn} disabled={busy || reason.trim().length < 3} onClick={() => change(key, false, reason)}>
                  Confirm OFF
                </button>
                <button className={styles.btn} disabled={busy} onClick={() => setConfirming(null)}>Cancel</button>
              </div>
            )}
          </div>
        );
      })}
      {history.length > 0 && (
        <>
          <div className={styles.histTitle}>Recent changes</div>
          <div className={styles.hist}>
            {history.slice(0, 8).map((h, i) => (
              <div key={i}>
                {features[h.feature]?.label || h.feature} → <b>{h.enabled ? 'ON' : 'OFF'}</b>
                {h.reason ? ` ("${h.reason}")` : ''} · {h.changed_by_name || 'admin'} · {new Date(h.changed_at).toLocaleString()}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default FeatureSwitchesPanel;